/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import React from 'react';

import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import APIUtil from 'util/APIUtil';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  COLUMN_TYPE_TO_NAME,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';

interface Props {
  containerSchemaId: string;
  onImportRows: (...args: Array<any>) => any;
  onRequestClose: (...args: Array<any>) => any;
  fileId: string;
  columnDefinitions: Array<ColumnDefinition>;
}

class BulkAddRowsToTransferTableFlow extends React.Component<Props> {
  static open(props: any): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkAddRowsToTransferTableFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getRequiredColumnsErrorForNotebookEntry = (columns: any): string | null => {
    const numSampleColumns = columns.filter(({type}: any) =>
      [COLUMN_TYPE.FILENAME, COLUMN_TYPE.BATCH_NAME].includes(type)
    ).length;
    const numSourceContainerColumns = columns.filter(
      ({type}: any) => type === COLUMN_TYPE.INPUT_CONTAINER_BARCODE
    ).length;
    const numSourceGridColumns = columns.filter(
      ({type}: any) => type === COLUMN_TYPE.INPUT_GRID_BARCODE
    ).length;
    const numSourcePositionColumns = columns.filter(
      ({type}: any) => type === COLUMN_TYPE.INPUT_GRID_COORDINATES
    ).length;

    if (numSampleColumns + numSourceContainerColumns + numSourceGridColumns !== 1) {
      return `Exactly one "${COLUMN_TYPE_TO_NAME[COLUMN_TYPE.FILENAME]}", "${
        COLUMN_TYPE_TO_NAME[COLUMN_TYPE.BATCH_NAME]
      }", "${COLUMN_TYPE_TO_NAME[COLUMN_TYPE.INPUT_CONTAINER_BARCODE]}", or "${
        COLUMN_TYPE_TO_NAME[COLUMN_TYPE.INPUT_GRID_BARCODE]
      }" field is required.`;
    }
    if (numSourceGridColumns || numSourcePositionColumns) {
      if (numSourceGridColumns !== 1) {
        return `Exactly one ${
          COLUMN_TYPE_TO_NAME[COLUMN_TYPE.INPUT_GRID_BARCODE]
        } field is required when specifying ${COLUMN_TYPE_TO_NAME[COLUMN_TYPE.INPUT_GRID_COORDINATES]}.`;
      }
      if (numSourcePositionColumns !== 1) {
        return `Exactly one ${
          COLUMN_TYPE_TO_NAME[COLUMN_TYPE.INPUT_GRID_COORDINATES]
        } field is required when specifying ${COLUMN_TYPE_TO_NAME[COLUMN_TYPE.INPUT_GRID_BARCODE]}.`;
      }
    }
    return null;
  };

  _getRequiredColumnsErrorForStageEntry = (columns: any): string | null => {
    const numStageRunSampleColumns = columns.filter(
      ({type}: any) => type === COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME
    ).length;
    if (numStageRunSampleColumns !== 1) {
      return `Exactly one "${COLUMN_TYPE_TO_NAME[COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME]}" field is required.`;
    }

    return null;
  };

  _getColumnTypesFromColumnDefinitions = (columnDefinitions: any): Array<COLUMN_TYPE> => {
    const columnTypes = [];
    for (const columnDefinition of columnDefinitions) {
      if (columnDefinition.type === TableColumnType.PARENT_STORABLE) {
        columnTypes.push(COLUMN_TYPE.PARENT_STORABLE_BARCODE);
      } else if (columnDefinition.type === TableColumnType.NUMBER_OF_CONTAINERS) {
        columnTypes.push(COLUMN_TYPE.NUMBER_OF_CONTAINERS);
      } else if (columnDefinition.type === TableColumnType.POSITION) {
        if (columnDefinition.columnGroup !== TransferTableColumnGroup.SOURCE) {
          columnTypes.push(COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES);
        }
      } else if (columnDefinition.type === TableColumnType.CONTAINERS) {
        if (columnDefinition.columnGroup !== TransferTableColumnGroup.SOURCE) {
          columnTypes.push(COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE);
        }
      } else if (columnDefinition.type === TableColumnType.QUANTITY) {
        columnTypes.push(COLUMN_TYPE.TRANSFER_QUANTITY);
      } else if (columnDefinition.type === TableColumnType.CONCENTRATION) {
        columnTypes.push(COLUMN_TYPE.INPUT_CONCENTRATION_VALUE);
      }
    }
    return columnTypes;
  };

  _getPanes: () => Array<Pane> = () => [
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({}),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes: () => [
          ...(APIUtil.isStageEntry(this.props.fileId)
            ? [COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME, COLUMN_TYPE.STAGE_RUN_NAME]
            : [
                COLUMN_TYPE.FILENAME,
                COLUMN_TYPE.BATCH_NAME,
                COLUMN_TYPE.INPUT_CONTAINER_BARCODE,
                COLUMN_TYPE.INPUT_GRID_BARCODE,
                COLUMN_TYPE.INPUT_GRID_COORDINATES,
              ]),

          // Same for stage entries and Notebook entries
          ...this._getColumnTypesFromColumnDefinitions(this.props.columnDefinitions),
          COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
          COLUMN_TYPE.IGNORE,
        ],
        getRequiredColumnsError: (columns: any) =>
          APIUtil.isStageEntry(this.props.fileId)
            ? this._getRequiredColumnsErrorForStageEntry(columns)
            : this._getRequiredColumnsErrorForNotebookEntry(columns),
        getValidSchemaFieldIds: () => {
          const schemaFieldIds = [];
          for (const columnDefinition of this.props.columnDefinitions) {
            if (columnDefinition.type === TableColumnType.TAG_VALUE) {
              schemaFieldIds.push(columnDefinition.tagSchemaFieldApiId);
            }
          }
          return schemaFieldIds;
        },
        onValidateSpreadsheet: () => {
          SpreadsheetBulkImportActions.validateNewRowsForTransferTableImport(this.props.fileId);
        },
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: false,
      }),
    },
  ];

  _handleImportFromSpreadsheet = (): void => {
    SpreadsheetBulkImportActions.getNewRowsForTransferTableImport(this.props.fileId, this.props.onImportRows);
  };

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="transfer table row"
        importType={IMPORT_TYPE.TRANSFER_TABLE}
        initialSettings={{
          initialContainerSchemaId: this.props.containerSchemaId,
        }}
        onImportFromSpreadsheet={this._handleImportFromSpreadsheet}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import into transfer table"
      />
    );
  }
}

export default BulkAddRowsToTransferTableFlow;
